// Icons
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ScheduleIcon from "@mui/icons-material/Schedule";
import simpleRestProvider from "ra-data-simple-rest";
import { Admin, fetchUtils, Resource } from "react-admin";
import { authProvider } from "./authProvider";
import { Dashboard } from "./components/Dashboard";
import { Layout } from "./Layout";
import {
	AirdropAllocationEdit,
	AirdropAllocationList,
	AirdropAllocationShow,
} from "./resources/AirdropAllocations";
import {
	ClaimTransactionList,
	ClaimTransactionShow,
} from "./resources/ClaimTransactions";
import { UserCreate, UserList, UserShow } from "./resources/Users";
import {
	VestingPlanCreate,
	VestingPlanEdit,
	VestingPlanList,
	VestingPlanShow,
} from "./resources/VestingPlans";

// Smart API URL detection: non-auth endpoints use /api (no /admin)
const getApiUrl = () => {
	const envUrl = process.env.REACT_APP_API_URL;
	if (envUrl) return envUrl.replace(/\/$/, "");
	return "/api";
};

const API_URL = getApiUrl();
console.log("Admin UI connecting to API:", API_URL);

// Custom httpClient with API key and proper JSON headers
const httpClient = (url: string, options: any = {}) => {
	const finalOptions = { ...options };
	finalOptions.headers = finalOptions.headers || new Headers();

	// Ensure Headers instance
	if (!(finalOptions.headers instanceof Headers)) {
		finalOptions.headers = new Headers(finalOptions.headers);
	}

	// Add JWT token if available
	const auth = localStorage.getItem("auth");
	if (auth) {
		const { token } = JSON.parse(auth);
		if (token) {
			finalOptions.headers.set("Authorization", `Bearer ${token}`);
		}
	}
	// Only set JSON headers if body is not FormData
	const isFormData =
		finalOptions.body &&
		typeof FormData !== "undefined" &&
		finalOptions.body instanceof FormData;
	if (!isFormData) {
		finalOptions.headers.set("Accept", "application/json");
		// Don't overwrite if Content-Type already set by caller
		if (!finalOptions.headers.has("Content-Type")) {
			finalOptions.headers.set("Content-Type", "application/json");
		}
	}

	return fetchUtils.fetchJson(url, finalOptions);
};

// Custom dataProvider for React Admin
const dataProvider = {
	...simpleRestProvider(API_URL, httpClient),
	getList: async (resource: string, params: any) => {
		console.log(
			"🔍 Fetching list for resource:",
			resource,
			"with params:",
			params,
		);

		// Map airdrop-allocations to users with filter
		if (resource === "airdrop-allocations") {
			const { page, perPage } = params.pagination;
			const { field, order } = params.sort;
			const filter = params.filter || {};

			// Add filter for users with assigned vesting
			const usersFilter = {
				...filter,
				isAssignedToVesting: true,
			};

			const start = (page - 1) * perPage;
			const end = page * perPage - 1;

			const url = `${API_URL}/users?${new URLSearchParams({
				filter: JSON.stringify(usersFilter),
				range: JSON.stringify([start, end]),
				sort: JSON.stringify([field, order]),
			})}`;

			const response = await httpClient(url, { method: "GET" });

			const json = response.json;
			console.log("📦 Airdrop allocations (from users) response:", json);

			// Transform users data to match airdrop allocation structure
			const transformedData = (json.data || []).map((user: any) => ({
				...user,
				metadata: {
					vestingId: user.assignedVestingId,
				},
			}));

			return {
				data: transformedData,
				total: json.__meta?.total || transformedData.length,
			};
		}

		// Custom handling for users resource
		if (resource === "users") {
			const { page, perPage } = params.pagination;
			const { field, order } = params.sort;
			const filter = params.filter || {};

			const start = (page - 1) * perPage;
			const end = page * perPage - 1;

			const url = `${API_URL}/${resource}?${new URLSearchParams({
				filter: JSON.stringify(filter),
				range: JSON.stringify([start, end]),
				sort: JSON.stringify([field, order]),
			})}`;

			const response = await httpClient(url, { method: "GET" });
			const json = response.json;
			console.log("📦 Users response:", json);

			// Transform data to use _id as id for React Admin
			const transformedData = (json.data || []).map((user: any) => ({
				...user,
				id: user._id || user.id, // Use MongoDB _id as id for React Admin
			}));

			return {
				data: transformedData,
				total: json.__meta?.total || json.data?.length || 0,
			};
		}

		return simpleRestProvider(API_URL, httpClient).getList(resource, params);
	},
	getOne: async (resource: string, params: any) => {
		console.log(
			"🔍 Fetching one for resource:",
			resource,
			"with params:",
			params,
		);

		// Map airdrop-allocations to users
		if (resource === "airdrop-allocations") {
			const url = `${API_URL}/users/${params.id}`;
			const response = await httpClient(url, { method: "GET" });
			const user = response.json;
			console.log("📦 Allocation (from user) response:", user);

			// Transform user data to match airdrop allocation structure
			const data = {
				...user,
				metadata: {
					vestingId: user.assignedVestingId,
				},
			};

			return { data };
		}

		// Custom handling for users resource
		if (resource === "users") {
			const url = `${API_URL}/${resource}/${params.id}`;
			const response = await httpClient(url, { method: "GET" });
			const data = response.json;
			console.log("📦 User response:", data);

			// Transform data to use _id as id for React Admin
			return {
				data: {
					...data,
					id: data._id || data.id, // Use MongoDB _id as id for React Admin
				},
			};
		}

		return simpleRestProvider(API_URL, httpClient).getOne(resource, params);
	},
	getMany: (resource: string, params: any) => {
		console.log(
			"🔍 Fetching many for resource:",
			resource,
			"with params:",
			params,
		);
		return simpleRestProvider(API_URL, httpClient).getMany(resource, params);
	},
	getManyReference: (resource: string, params: any) => {
		console.log(
			"🔍 Fetching many reference for resource:",
			resource,
			"with params:",
			params,
		);
		return simpleRestProvider(API_URL, httpClient).getManyReference(
			resource,
			params,
		);
	},
	create: (resource: string, params: any) => {
		console.log("🔍 Creating for resource:", resource, "with params:", params);
		return simpleRestProvider(API_URL, httpClient).create(resource, params);
	},
	update: (resource: string, params: any) => {
		console.log("🔍 Updating for resource:", resource, "with params:", params);
		return simpleRestProvider(API_URL, httpClient).update(resource, params);
	},
	updateMany: (resource: string, params: any) => {
		console.log(
			"🔍 Updating many for resource:",
			resource,
			"with params:",
			params,
		);
		return simpleRestProvider(API_URL, httpClient).updateMany(resource, params);
	},
	delete: async (resource: string, params: any) => {
		console.log("🔍 Deleting for resource:", resource, "with params:", params);

		// For users, use _id as the ID
		if (resource === "users") {
			const url = `${API_URL}/${resource}/${params.id}`;
			console.log("🗑️ DELETE request to:", url, "ID:", params.id);
			await httpClient(url, { method: "DELETE" });
			return { data: params.previousData };
		}

		return simpleRestProvider(API_URL, httpClient).delete(resource, params);
	},
	deleteMany: (resource: string, params: any) => {
		console.log(
			"🔍 Deleting many for resource:",
			resource,
			"with params:",
			params,
		);
		return simpleRestProvider(API_URL, httpClient).deleteMany(resource, params);
	},
};

function App() {
	return (
		<Admin
			dataProvider={dataProvider}
			authProvider={authProvider}
			dashboard={Dashboard}
			layout={Layout}
			title="Atleta Network Admin"
		>
			<Resource
				name="users"
				list={UserList}
				show={UserShow}
				create={UserCreate}
				icon={PersonIcon}
				options={{ label: "Users" }}
			/>
			<Resource
				name="airdrop-allocations"
				list={AirdropAllocationList}
				show={AirdropAllocationShow}
				edit={AirdropAllocationEdit}
				icon={PeopleIcon}
				options={{ label: "Airdrop Allocations" }}
			/>
			<Resource
				name="claim-transactions"
				list={ClaimTransactionList}
				show={ClaimTransactionShow}
				icon={ReceiptIcon}
				options={{ label: "Claim Transactions" }}
			/>
			<Resource
				name="vesting-plans"
				list={VestingPlanList}
				show={VestingPlanShow}
				edit={VestingPlanEdit}
				create={VestingPlanCreate}
				icon={ScheduleIcon}
				options={{ label: "Vesting Plans" }}
			/>
		</Admin>
	);
}

export default App;
